import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import IdealStudio from "../../../../core/editor/main";
import { isSlide, isContainedView } from "../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../common/constants";
import { randomPositionGenerator } from "../../clipboard/clipboard.utils";
import { createBox } from "../../../../common/common_tools";
import getMediaBoxSize from "../../../utils/getMediaBoxSize";

function YoutubePanel({
  onElementSelected,
  onBoxAdded,
  boxes,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  boxSelected,
  pluginToolbars,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [msg, setMsg] = useState(i18n.t("FileModal.APIProviders.no_files"));
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const hasResults = useMemo(() => results && results.length > 0, [results]);

  const handleSearch = useCallback(
    async (text) => {
      const q = (text || query || "").trim();
      if (!q) return;

      try {
        setLoading(true);
        setMsg(i18n.t("FileModal.APIProviders.searching"));

        const url =
          "https://www.googleapis.com/youtube/v3/search?part=id,snippet&maxResults=20&q=" +
          encodeURIComponent(q) +
          "&key=AIzaSyCnCtUmlZ432Ak88LohJoSAZygWw-JN4rc&videoEmbeddable=true&type=video";

        const res = await fetch(url);
        const videos = await res.json();

        if (videos && Array.isArray(videos.items)) {
          const mapped = videos.items.map((video) => ({
            title: video.snippet.title,
            channelTitle: video.snippet.channelTitle,
            publishedAt: video.snippet.publishedAt,
            url: "https://www.youtube.com/embed/" + (video.id ? video.id.videoId : ""),
            thumbnail:
              video.snippet &&
              video.snippet.thumbnails &&
              video.snippet.thumbnails.default &&
              video.snippet.thumbnails.default.url
                ? video.snippet.thumbnails.default.url
                : "",
          }));
          setResults(mapped);
          setMsg(mapped.length > 0 ? "" : i18n.t("FileModal.APIProviders.no_files"));
        } else {
          setResults([]);
          setMsg(i18n.t("FileModal.APIProviders.no_files"));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setResults([]);
        setMsg(i18n.t("FileModal.APIProviders.error"));
      } finally {
        setLoading(false);
      }
    },
    [query]
  );

  const addVideoToScene = async (videoUrl) => {
    if (!onBoxAdded || !videoUrl || !boxes || !navItems || !containedViews) return;

    const pluginName = "EnrichedPlayer";
    const plugin = IdealStudio.Plugins.get(pluginName);
    if (!plugin) return;

    const navItem =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems[navItemSelected];

    if (!navItem) return;

    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);
    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(videoUrl, "video");

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      position: {
        x: randomPositionGenerator(10, 30),
        y: randomPositionGenerator(10, 30),
        type: "absolute",
      },
      id: ID_PREFIX_BOX + Date.now(),
      page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
      url: videoUrl,
      width,
      height,
      widthUnit,
      heightUnit,
    };

    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };

  const handleResultDoubleClick = (item) => {
    addVideoToScene(item.url);

    if (onElementSelected) {
      onElementSelected(item.title, item.url, "video", undefined);
    }
  };

  return (
    <div
      className="library-youtube-panel"
      style={{
        flex: 1,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          title={i18n.t("close") || "Kapat"}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 24,
            height: 24,
            borderRadius: 999,
            border: "1px solid #e5e7eb",
            backgroundColor: "rgba(255,255,255,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 10,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            stroke="#6b7280"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {i18n.t("youtube") || "Youtube"}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <input
            type="text"
            placeholder={i18n.t("youtube_search_placeholder") || "Ara"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: "100%",
              height: 36,
              padding: "0 10px",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              outline: "none",
              fontSize: 13,
              backgroundColor: "#f9fafb",
            }}
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            style={{
              height: 34,
              borderRadius: 999,
              border: "none",
              backgroundColor: loading ? "#cbd5f5" : "#6366f1",
              color: "white",
              fontSize: 13,
              fontWeight: 500,
              cursor: loading || !query.trim() ? "default" : "pointer",
            }}
          >
            {loading
              ? i18n.t("FileModal.APIProviders.searching") || "Aranıyor..."
              : i18n.t("youtube_search_button") || "Ara"}
          </button>
        </form>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          paddingRight: 4,
        }}
      >
        {!hasResults && msg && (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            {msg}
          </div>
        )}

        {hasResults && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {results.map((item, index) => {
              const date = new Date(item.publishedAt);
              return (
                <button
                  key={index}
                  type="button"
                  onDoubleClick={() => handleResultDoubleClick(item)}
                  onClick={() => setPreviewUrl(item.url)}
                  style={{
                    textAlign: "left",
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    backgroundColor: "#ffffff",
                    padding: 8,
                    cursor: "pointer",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      flexShrink: 0,
                      width: 80,
                      height: 50,
                      borderRadius: 6,
                      overflow: "hidden",
                      backgroundColor: "#e5e7eb",
                    }}
                  >
                    {item.thumbnail ? (
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : null}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        marginBottom: 4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        marginBottom: 2,
                      }}
                    >
                      {item.channelTitle}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        marginBottom: 8,
                      }}
                    >
                      {date.toLocaleString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResultDoubleClick(item);
                      }}
                      style={{
                        fontSize: 11,
                        padding: "6px 12px",
                        borderRadius: 6,
                        border: "none",
                        backgroundColor: "#6366f1",
                        color: "white",
                        cursor: "pointer",
                        fontWeight: 500,
                        width: "100%",
                      }}
                    >
                      {i18n.t("add_to_scene") || "Sahneye Ekle"}
                    </button>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {previewUrl && (
        <div
          style={{
            marginTop: 12,
            borderRadius: 10,
            overflow: "hidden",
            backgroundColor: "#000",
            aspectRatio: "16 / 9",
          }}
        >
          <iframe
            title="youtube-preview"
            width="100%"
            height="100%"
            src={previewUrl}
            frameBorder={0}
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
}

YoutubePanel.propTypes = {
  onElementSelected: PropTypes.func,
  onBoxAdded: PropTypes.func,
  boxes: PropTypes.object,
  navItems: PropTypes.object,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containedViews: PropTypes.object,
  containedViewSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  boxSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pluginToolbars: PropTypes.object,
  onClose: PropTypes.func,
};

export default YoutubePanel;
